"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Testimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const testimonials = [{
    quote: "Tiny to Teen Services provided a neutral, safe environment that allowed our child to feel comfortable during visits. Their professionalism helped us navigate a very difficult transition with clarity.",
    name: "Parent A",
    role: "Family Support Client",
    date: "March 2024"
  }, {
    quote: "As a case manager, I rely on their detailed, objective observation reports. They maintain firm boundaries while remaining deeply child-centered, which is exactly what our court cases require.",
    name: "Sarah Jenkins",
    role: "Case Manager",
    date: "January 2024"
  }, {
    quote: "The team at Tiny to Teen Services is incredibly trauma-informed. They handled our high-conflict situation with such calm and steady professionalism that it truly lowered the temperature for everyone involved.",
    name: "Parent B",
    role: "Supervised Parenting Client",
    date: "November 2023"
  }, {
    quote: "Their monitored exchange services are seamless. It is a relief to have a neutral third party ensuring that transitions are safe, structured, and respectful for our children.",
    name: "David Miller",
    role: "Monitored Exchange Client",
    date: "October 2023"
  }, {
    quote: "I appreciate how they prioritize the child's experience above all else. Their approach to reunification support is compassionate, grounded, and always focused on the best interests of the family.",
    name: "Dr. Elena Rodriguez",
    role: "Family Therapist",
    date: "August 2023"
  }];
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const progress = (viewportH - rect.top) / (viewportH + rect.height);
      setScrollY(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const progressPercent = count > 1 ? (current + 1) / count * 100 : 0;
  const parallaxOffset = (scrollY - 0.5) * 60;
  return <section data-section-id="3329" ref={sectionRef} id="familytestimonials" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="h-px w-16 md:w-24 bg-primary" aria-hidden="true" />
            <span className="font-default text-[11px] tracking-[0.25em] uppercase text-secondary">
              Testimonials &mdash; Trust
            </span>
            <span className="h-px w-16 md:w-24 bg-primary" aria-hidden="true" />
          </div>

          <h2 className="font-default font-semibold text-foreground leading-[1.1] tracking-tight text-4xl md:text-6xl lg:text-7xl">
            <span className="block">Voices of</span>
            <span className="block italic text-secondary">
              families we support
            </span>
          </h2>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "start",
        loop: false
      }} className="w-full">
          <CarouselContent className="-ml-6 md:-ml-10">
            {testimonials.map((t, i) => <CarouselItem key={i} data-index={i} className="pl-6 md:pl-10 basis-full md:basis-1/2 lg:basis-1/3">
                <article className="relative h-full flex flex-col bg-card p-8 rounded-lg shadow-md">
                  <div aria-hidden="true" className="font-default leading-none select-none pointer-events-none text-primary" style={{
                fontSize: "clamp(4rem, 8vw, 6rem)",
                transform: `translateY(${parallaxOffset}px)`,
                transition: "transform 0.1s linear",
                lineHeight: 0.8,
                marginLeft: "-0.1em"
              }}>
                    &ldquo;
                  </div>

                  <blockquote className="mt-2 md:mt-4">
                    <p className="font-default text-foreground leading-[1.4] text-xl md:text-2xl font-medium">
                      {t.quote}
                    </p>
                  </blockquote>

                  <div className="mt-8" />

                  <div className="h-px w-10 bg-secondary" aria-hidden="true" />

                  <div className="mt-6 space-y-2">
                    <div className="font-default text-sm font-bold text-foreground">
                      {t.name}
                    </div>
                    <div className="font-default text-[11px] tracking-[0.2em] uppercase text-secondary">
                      {t.role}
                    </div>
                    <div className="font-default text-[11px] tracking-[0.15em] uppercase text-secondary/70">
                      {t.date}
                    </div>
                  </div>
                </article>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>

        <div className="mt-20 md:mt-24">
          <div className="flex items-center gap-8 md:gap-12">
            <div className="flex-1 relative">
              <div className="h-px w-full bg-primary" aria-hidden="true" />
              <div className="absolute top-0 left-0 h-px transition-all duration-500 ease-out bg-foreground" style={{
              width: `${progressPercent}%`
            }} aria-hidden="true" />
            </div>

            <div className="font-default text-[11px] tracking-[0.2em] uppercase text-secondary tabular-nums shrink-0">
              {String(current + 1).padStart(2, "0")}
              <span className="mx-2 text-secondary/50">/</span>
              {String(count || testimonials.length).padStart(2, "0")}
            </div>

            <div className="flex items-center gap-6 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => api?.scrollPrev()} disabled={!api?.canScrollPrev()} className="font-default text-xs tracking-[0.2em] uppercase text-foreground hover:bg-transparent hover:text-secondary disabled:opacity-30 px-2 h-auto py-1 rounded-md" aria-label="Previous testimonial">
                &larr; Prev
              </Button>
              <Button variant="ghost" size="sm" onClick={() => api?.scrollNext()} disabled={!api?.canScrollNext()} className="font-default text-xs tracking-[0.2em] uppercase text-foreground hover:bg-transparent hover:text-secondary disabled:opacity-30 px-2 h-auto py-1 rounded-md" aria-label="Next testimonial">
                Next &rarr;
              </Button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/ContactPage" className="font-default text-xs tracking-[0.2em] uppercase text-secondary hover:text-foreground transition-colors duration-300 inline-flex items-center gap-2">
              Contact us for support &rarr;
            </Link>
          </div>
        </div>
      </div>
    </section>;
}